import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Avatar } from '../components/Avatar';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { can, ROLE_LABELS, MEMBER_TYPES, MEMBER_TYPE_LABELS, memberTypeLabel, type Role, type MemberType } from '@gwc/shared';
import { membersApi, type InviteInput, type PersonLookupRow } from '../api/members';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import type { MemberRow } from '../api/models';
import { useAuth } from '../auth/AuthProvider';
import { ApiError } from '../lib/api';

export function Members() {
  const [filter, setFilter] = useState('');
  const { user } = useAuth();
  const canManage = user ? can(user.roles, 'users:manage') : false;
  const { data: members = [], isLoading } = useQuery({ queryKey: ['members'], queryFn: () => membersApi.list() });

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold-400">Directory</p>
          <h1 className="mt-2 font-display text-3xl text-cream-50">Members</h1>
          <input
            className="field mt-4 max-w-sm"
            placeholder="Find a member…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Filter members"
          />
        </div>
        <span className="text-sm text-cream-300/50">{members.length} members</span>
      </div>

      {canManage && (
        <div className="flex flex-wrap gap-3">
          <InviteForm />
          <InvitePastMember />
        </div>
      )}

      {isLoading ? (
        <p className="text-cream-300/60">Loading…</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {members
            .filter((m) =>
              `${m.firstName} ${m.lastName}`.toLowerCase().includes(filter.trim().toLowerCase()),
            )
            .map((m) => (
              <MemberCard key={m.id} member={m} />
          ))}
        </div>
      )}
    </div>
  );
}

function MemberCard({ member }: { member: MemberRow }) {
  const status = member.accountStatus ?? 'no login';
  return (
    <Link to={`/members/${member.id}`} className="card flex items-center justify-between gap-3 p-4 transition hover:border-gold-600/40">
      <Avatar
        firstName={member.firstName}
        lastName={member.lastName}
        avatarUrl={member.avatarUrl}
        size="md"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-cream-50">
            {member.firstName} {member.lastName}
          </span>
          {member.memberType && (
            <span className="shrink-0 rounded-full border border-gold-600/30 px-2 py-0.5 text-[10px] text-gold-200">
              {memberTypeLabel(member.memberType)}
            </span>
          )}
        </div>
        <div className="mt-0.5 truncate text-xs text-cream-300/50">
          {member.email ?? 'no email'} · {status}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {member.roles.map((r) => (
            <span key={r} className="rounded bg-ink-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-cream-300/60">
              {ROLE_LABELS[r as Role] ?? r}
            </span>
          ))}
        </div>
      </div>
      <span className="shrink-0 text-cream-300/40" aria-hidden>
        →
      </span>
    </Link>
  );
}

function InviteForm() {
  const qc = useQueryClient();
  const [form, setForm] = useState<InviteInput>({
    email: '',
    firstName: '',
    lastName: '',
    memberType: 'member_general',
    roles: ['user'],
  });
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const invite = useMutation({
    mutationFn: () => membersApi.invite(form),
    onSuccess: (res) => {
      setLink(res.acceptUrl);
      setForm({ email: '', firstName: '', lastName: '', memberType: 'member_general', roles: ['user'] });
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not send invite'),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLink(null);
    invite.mutate();
  }
  const set = (patch: Partial<InviteInput>) => setForm((f) => ({ ...f, ...patch }));

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn-ghost mb-6 text-sm">
        + Invite a member
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="card mb-6 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm uppercase tracking-wide text-gold-400">Invite a member</h2>
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-cream-300/50 hover:text-cream-100">
          Close
        </button>
      </div>
      {error && <p className="text-sm text-wine-300">{error}</p>}
      {link && (
        <p className="rounded border border-gold-600/40 bg-ink-950 px-3 py-2 text-xs text-gold-200">
          Invitation link (share it if the email doesn’t arrive):
          <br />
          <span className="break-all text-cream-100">{link}</span>
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <input className="field" placeholder="First name" value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} required />
        <input className="field" placeholder="Last name" value={form.lastName} onChange={(e) => set({ lastName: e.target.value })} required />
        <input className="field" type="email" placeholder="Email" value={form.email} onChange={(e) => set({ email: e.target.value })} required />
        <select className="field" value={form.memberType ?? 'member_general'} onChange={(e) => set({ memberType: e.target.value as MemberType })}>
          {MEMBER_TYPES.map((t) => (
            <option key={t} value={t}>
              {MEMBER_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
      </div>
      <button type="submit" className="btn-primary" disabled={invite.isPending}>
        {invite.isPending ? 'Inviting…' : 'Create invitation'}
      </button>
    </form>
  );
}

/**
 * Invite someone who is already in the records — a past member whose scorecards
 * were imported years ago — so their history is attached to the account they claim.
 */
function InvitePastMember() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const debounced = useDebouncedValue(q, 250);
  const [picked, setPicked] = useState<PersonLookupRow | null>(null);
  const [email, setEmail] = useState('');
  const [memberType, setMemberType] = useState<MemberType>('past_member');
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);

  const { data: matches = [] } = useQuery({
    queryKey: ['people-lookup', debounced, 'admin'],
    queryFn: () => membersApi.lookup(debounced, 12),
    enabled: open && debounced.trim().length >= 2,
  });
  const candidates = matches.filter((p) => !p.accountStatus);

  const invite = useMutation({
    mutationFn: () => membersApi.inviteExisting(picked!.id, { email: email.trim() || undefined, memberType }),
    onSuccess: (res) => {
      setLink(res.acceptUrl);
      setError(res.emailSent ? null : 'Email could not be delivered — share the link below.');
      setPicked(null);
      setQ('');
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not send invite'),
  });

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn-ghost mb-6 text-sm">
        + Invite a past member
      </button>
    );
  }
  return (
    <form
      className="card mb-6 w-full space-y-3"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setError(null);
        setLink(null);
        if (picked) invite.mutate();
      }}
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm uppercase tracking-wide text-gold-400">Invite a past member</h2>
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-cream-300/50 hover:text-cream-100">
          Close
        </button>
      </div>
      <p className="text-xs text-cream-300/60">
        For people already in the records (from imported scorecards, say) who have never had a login. Their history stays attached.
      </p>
      {error && <p className="text-sm text-wine-300">{error}</p>}
      {link && (
        <p className="rounded border border-gold-600/40 bg-ink-950 px-3 py-2 text-xs text-gold-200">
          Invitation link (share it if the email doesn’t arrive):
          <br />
          <span className="break-all text-cream-100">{link}</span>
        </p>
      )}
      {!picked ? (
        <>
          <input className="field" placeholder="Search the records by name…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          {debounced.trim().length >= 2 && (
            <ul className="max-h-48 divide-y divide-ink-800 overflow-auto rounded border border-ink-800 text-sm">
              {candidates.length === 0 && <li className="px-3 py-2 text-cream-300/50">No one without an account matches.</li>}
              {candidates.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-ink-900/60"
                    onClick={() => {
                      setPicked(p);
                      setEmail(p.email ?? '');
                    }}
                  >
                    <span className="text-cream-100">
                      {p.firstName} {p.lastName}
                    </span>
                    <span className="text-xs text-cream-300/50">{p.email ?? 'no email on file'}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="field flex items-center justify-between">
            <span>
              {picked.firstName} {picked.lastName}
            </span>
            <button type="button" className="text-xs text-cream-300/50 hover:text-cream-100" onClick={() => setPicked(null)}>
              change
            </button>
          </div>
          <input className="field" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <select className="field" value={memberType} onChange={(e) => setMemberType(e.target.value as MemberType)}>
            {MEMBER_TYPES.map((t) => (
              <option key={t} value={t}>
                {MEMBER_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
      )}
      <button type="submit" className="btn-primary" disabled={!picked || invite.isPending}>
        {invite.isPending ? 'Inviting…' : 'Send invitation'}
      </button>
    </form>
  );
}
